import {
  Component,
  HostListener,
  Output,
  OnInit,
  DoCheck,
  QueryList,
  ViewChildren,
  EventEmitter
} from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Observable, of, Subscription, BehaviorSubject } from 'rxjs';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import {
  type OBTAirportDetails,
  type OBTAirlinesDetails,
  type FlightEnterpriseSearchInterface,
  FlightSearchType,
  TimeWindow,
  type IMultiDestination,
  LocationTypes,
  type UserServices,
  MenuSlideEnum
} from '@sabstravtech/obtservices/base';
import {
  EnterpriseSearchService,
  UserService,
  ServiceType,
  HelperRoutines
} from '@sabstravtech/obtservices/angular';
import { debounceTime, distinctUntilChanged, switchMap, merge } from 'rxjs/operators';
import _ from 'lodash';

import { BaseComponent } from '../../../core/components/base_components/base-componet';
import { DeviceDetector } from '../../../core/services/device-detector.service';
import { Helpers } from '../../../core/classes/helpers';
import { LightningUserFavorurite } from '../../../core/classes/user-favourite.enum';
import { FlightPipe } from '../../../startup/pipes/flight-pipe';

@Component({
  selector: 'app-flight-search',
  templateUrl: './flight-search.component.html',
  styleUrls: ['./flight-search.component.scss'],
  animations: [
    trigger('slideUpAndDown', [
      state(MenuSlideEnum.IN, style({ transform: 'translateY(0)' })),
      transition('void => *', [
        style({ transform: 'translateY(100%)' }), //
        animate(200)
      ]),
      transition('* => void', [animate(200, style({ transform: 'translateY(50%)' }))])
    ])
  ],
  standalone: false
})
export class FlightSearchComponent extends BaseComponent implements OnInit, DoCheck {
  @ViewChildren(NgbInputDatepicker) datepickerList: QueryList<NgbInputDatepicker>;

  focusVia$ = new Subject<string>();
  focus$ = new Subject<string>();
  focus2$ = new Subject<string>();
  focus3$ = new Subject<string>();
  focus4$ = new Subject<string>();
  focus5$ = new Subject<string>();
  focus6$ = new Subject<string>();
  focus7$ = new Subject<string>();
  focus8$ = new Subject<string>();

  testArrayVia: Observable<OBTAirportDetails[]> = of([]);
  testArray: Observable<OBTAirportDetails[]> = of([]);
  testArray2: Observable<OBTAirportDetails[]> = of([]);
  testArray4: Observable<OBTAirportDetails[]> = of([]);
  testArray3: Observable<OBTAirportDetails[]> = of([]);
  firstAirlineArray: Observable<OBTAirlinesDetails[]> = of([]);
  secondAirlineArray: Observable<OBTAirlinesDetails[]> = of([]);
  thirdAirlineArray: Observable<OBTAirlinesDetails[]> = of([]);
  fourthAirlineArray: Observable<OBTAirlinesDetails[]> = of([]);
  testflightVias: Observable<OBTAirportDetails[]> = of([]);
  departLocationIsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  arriveLocationIsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  viaLocationIsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  step = 30;
  showNearby: boolean;
  showVia = true;
  showAnytime = false;
  criteria: string[];
  userProfileSubscription: Subscription;
  searchParams: FlightEnterpriseSearchInterface;
  TimeWindow: typeof TimeWindow = TimeWindow;
  FlightSearchType: typeof FlightSearchType = FlightSearchType;
  multiCitySearch: IMultiDestination[] = [];
  /** Tracks the externally-set multiDestination shape so we can re-sync the
   *  local `multiCitySearch` form state when the chatbot (or any other source)
   *  populates the flight search programmatically. */
  private _lastMultiCitySignature = '';
  minimumMultiCityDate: NgbDateStruct;
  showMultiCity = false;
  showFavourites = false;
  flexibilityHours: number[] = [];
  hotelsearchRequested = false;
  displayHotelCheck = false;
  @Output() toggleHotelSearchRequested: EventEmitter<boolean> = new EventEmitter();
  hotelTown = '';
  ServiceType = ServiceType;
  private userSetDirectValue = false;
  maxSectors: number;
  services: UserServices;
  constructor(
    public searchService: EnterpriseSearchService,
    public userService: UserService,
    public deviceDetector: DeviceDetector,
    protected flightPipe: FlightPipe,
    title: Title,
    public translateService: TranslateService,
    private helpers: HelperRoutines
  ) {
    super(title, translateService);
  }

  ngOnInit(): void {
    this.getDefaultJourneyTimes();

    this.searchParams = this.searchService.searches[ServiceType.Flight];
    if (this.searchParams.via) {
      this.searchParams.maxConnections = null;
    } else if (this.searchParams.directDefaultValue) {
      this.searchParams.maxConnections = 0;
    } else {
      this.searchParams.maxConnections = null;
    }
    this.showVia = !this.userService.isUserFavoriteSet(LightningUserFavorurite.HideFlightVia);

    // let defPeriod = this.userService.getUserFavoriteValue(LightningUserFavorurite.DefFlightTimePeriod) || 'Anytime';

    this.showAnytime = this.userService.isUserFavoriteSet(
      LightningUserFavorurite.ShowFlightAnytimePeriod
    );
    this.services = this.userService.fullUserDetails.userServices.value;

    const flightSearchDefaults = this.userService.getUserFavoriteObject(
      LightningUserFavorurite.FlightSearchDefaults
    ) as {
      enableMultiCityForFlight: boolean;
      multiCityMaxSelectors: string;
      setMinimumTimeWindow: string;
      setDefaultTimeWindow: string;
      disableSearchForDifferentReturnAirports: boolean;
    };

    if (flightSearchDefaults) {
      const minimumTimeWindow = flightSearchDefaults?.setMinimumTimeWindow
        ? Number(flightSearchDefaults.setMinimumTimeWindow)
        : null;
      const defaultTimeWindow = flightSearchDefaults?.setDefaultTimeWindow
        ? Number(flightSearchDefaults.setDefaultTimeWindow)
        : null;

      this.showMultiCity = flightSearchDefaults?.enableMultiCityForFlight;
      if (this.showMultiCity) {
        this.maxSectors = flightSearchDefaults?.multiCityMaxSelectors
          ? Number(flightSearchDefaults?.multiCityMaxSelectors)
          : 6;
      }
      if (flightSearchDefaults.setMinimumTimeWindow) {
        this.searchParams.inTimeFlexibility = Number(flightSearchDefaults.setMinimumTimeWindow);
        for (let i = 1; i <= 7; i++) {
          if (!minimumTimeWindow || minimumTimeWindow <= i) {
            this.flexibilityHours.push(i);
          }
        }
      } else {
        for (let i = 1; i <= 7; i++) {
          this.flexibilityHours.push(i);
        }
      }

      if (defaultTimeWindow && defaultTimeWindow >= minimumTimeWindow) {
        this.searchParams.outTimeFlexibility = defaultTimeWindow;
        this.searchParams.inTimeFlexibility = defaultTimeWindow;
      } else if (minimumTimeWindow) {
        this.searchParams.outTimeFlexibility = minimumTimeWindow;
        this.searchParams.inTimeFlexibility = minimumTimeWindow;
      }
    } else {
      for (let i = 1; i <= 7; i++) {
        this.flexibilityHours.push(i);
      }
    }

    if (this.searchParams.chosenSearchType === FlightSearchType.multiCity) {
      this.setUpMultiCitySearch();
      this._lastMultiCitySignature = this.multiCitySignature();
    }
    this.showFavouritesOption();

    if (this.searchParams.isFlightHotelComboSearch) {
      this.hotelsearchRequested = true;
      this.toggleHotelSearchRequested.emit(true);
    }
  }

  /**
   * Re-sync the local `multiCitySearch` form rows when the underlying
   * `searchParams.multiDestination` changes from an external source (the
   * chatbot pre-fills it via `ChatbotFlightSearchService` and the form
   * otherwise has no way to know). Cheap signature check keeps this safe to
   * run on every change-detection cycle.
   */
  ngDoCheck(): void {
    if (!this.searchParams) return;
    if (this.searchParams.chosenSearchType !== FlightSearchType.multiCity) {
      this._lastMultiCitySignature = '';
      return;
    }
    const sig = this.multiCitySignature();
    if (sig && sig !== this._lastMultiCitySignature) {
      this.setUpMultiCitySearch();
      this._lastMultiCitySignature = sig;
    }
  }

  private multiCitySignature(): string {
    const md = this.searchParams?.multiDestination ?? [];
    return md
      .map((d: IMultiDestination) => {
        const orig = (d?.orig as OBTAirportDetails)?.gateway || '';
        const dest = (d?.dest as OBTAirportDetails)?.gateway || '';
        const date = d?.date ? moment(d.date).format('YYYY-MM-DD') : '';
        return `${orig}>${dest}@${date}`;
      })
      .join('|');
  }

  showFavouritesOption() {
    this.searchService.isFavoritesSelected = false;
    this.showFavourites = (
      this.userService.getUserFavoriteObject(LightningUserFavorurite.FlightSearchDefaults) as {
        enableFavoritesOptionForFlights: boolean;
      }
    )?.enableFavoritesOptionForFlights;
  }

  getDefaultJourneyTimes(): void {
    // this.searchService.flight.flights[0].time = '09:00';
    // this.searchService.flight.flights[1].time = '18:00';
    const outboundFave = this.userService.getUserFavoriteValue(
      LightningUserFavorurite.DefaultTimeOutbound
    );
    const inboundFave = this.userService.getUserFavoriteValue(
      LightningUserFavorurite.DefaultTimeInbound
    );
    if (outboundFave) {
      if (this.isValidTime(outboundFave)) {
        // this.searchService.flight.flights[0].time = this.roundToNearest30(outboundFave);
      }
    }
    if (inboundFave) {
      if (this.isValidTime(inboundFave)) {
        // this.searchService.flight.flights[1].time = this.roundToNearest30(inboundFave);
      }
    }
  }

  isValidTime(favouriteTime: string): boolean {
    return !!favouriteTime.match(/^(\d{2}):(\d{2})$/);
  }

  roundToNearest30(time: string) {
    const m = moment(`2018-12-08 ${time}`);
    return m.minute() >= 30 ? m.minutes(30).format('HH:mm') : m.minutes(0).format('HH:mm');
  }

  @HostListener('document:click', ['$event'])
  public onClick(event): void {
    Helpers.closeOpenCalendars(this.datepickerList, event);
  }

  // selectTakeOffLocation = (location: string, flightId: number): void =>
  //   this.searchService.flight.setFlightLocation(location, flightId, 'take_off_location');

  formatter_rail_air = (x: { destination: string }) => this.flightPipe.transform(x.destination);
  formatter_airlines = (x: { name: string }) => x.name;

  flightLocationsVia = (text$: Observable<string>): Observable<OBTAirportDetails[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focusVia$),
      switchMap((term: string): Observable<OBTAirportDetails[]> => {
        return term.length < 2
          ? this.testArrayVia
          : (this.testArrayVia = this.searchService.getAirportsList(
              term,
              null,
              this.viaLocationIsLoading
            ));
      })
    );

  flightLocations = (text$: Observable<string>): Observable<OBTAirportDetails[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focus$),
      switchMap((term: string): Observable<OBTAirportDetails[]> =>
        term.length < 2
          ? this.testArray
          : (this.testArray = this.searchService.getAirportsList(
              term,
              null,
              this.departLocationIsLoading
            ))
      )
    );

  flightLocations2 = (text$: Observable<string>): Observable<OBTAirportDetails[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focus2$),
      switchMap((term: string): Observable<OBTAirportDetails[]> =>
        term.length <= 2
          ? this.testArray2
          : (this.testArray2 = this.searchService.getAirportsList(
              term,
              null,
              this.arriveLocationIsLoading
            ))
      )
    );

  flightLocations3 = (text$: Observable<string>): Observable<OBTAirportDetails[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focus3$),
      switchMap((term: string): Observable<OBTAirportDetails[]> =>
        term.length <= 2
          ? this.testArray3
          : (this.testArray3 = this.searchService.getAirportsList(
              term,
              null,
              this.arriveLocationIsLoading
            ))
      )
    );

  flightLocations4 = (text$: Observable<string>): Observable<OBTAirportDetails[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focus4$),
      switchMap((term: string): Observable<OBTAirportDetails[]> =>
        term.length <= 2
          ? this.testArray4
          : (this.testArray4 = this.searchService.getAirportsList(
              term,
              null,
              this.arriveLocationIsLoading
            ))
      )
    );

  firstAirline = (text$: Observable<string>): Observable<OBTAirlinesDetails[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focus5$),
      switchMap((term: string): Observable<OBTAirlinesDetails[]> =>
        term.length <= 2
          ? this.firstAirlineArray
          : (this.firstAirlineArray = this.searchService.getAirlinesList(
              term,
              this.arriveLocationIsLoading
            ))
      )
    );

  secondAirline = (text$: Observable<string>): Observable<OBTAirlinesDetails[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focus6$),
      switchMap((term: string): Observable<OBTAirlinesDetails[]> =>
        term.length <= 2
          ? this.secondAirlineArray
          : (this.secondAirlineArray = this.searchService.getAirlinesList(
              term,
              this.arriveLocationIsLoading
            ))
      )
    );

  thirdAirline = (text$: Observable<string>): Observable<OBTAirlinesDetails[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focus7$),
      switchMap((term: string): Observable<OBTAirlinesDetails[]> =>
        term.length <= 2
          ? this.thirdAirlineArray
          : (this.thirdAirlineArray = this.searchService.getAirlinesList(
              term,
              this.arriveLocationIsLoading
            ))
      )
    );

  fourthAirline = (text$: Observable<string>): Observable<OBTAirlinesDetails[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focus8$),
      switchMap((term: string): Observable<OBTAirlinesDetails[]> =>
        term.length <= 2
          ? this.fourthAirlineArray
          : (this.fourthAirlineArray = this.searchService.getAirlinesList(
              term,
              this.arriveLocationIsLoading
            ))
      )
    );

  hideFlightFarePreferences(): boolean {
    return this.userService.isUserFavoriteSet(LightningUserFavorurite.HideFlightFarePreferences);
  }

  ensureElementIsScrolledTo(event) {
    try {
      const typeAheadList = event.target.nextElementSibling;
      const activeButton = typeAheadList.getElementsByClassName('active')[0];
      if (
        activeButton.offsetTop + activeButton.clientHeight >
        typeAheadList.clientHeight + typeAheadList.scrollTop
      ) {
        typeAheadList.scrollTop =
          activeButton.offsetTop + activeButton.clientHeight - typeAheadList.clientHeight;
      } else if (activeButton.offsetTop < typeAheadList.scrollTop) {
        typeAheadList.scrollTop = activeButton.offsetTop;
      }
    } catch (e) {
      // tslint:disable-next-line: quotemark
      console.log("Couldn't find elements to scroll");
    }
  }

  direct(direct: boolean): void {
    this.userSetDirectValue = true;
    if (this.searchParams.via) {
      this.searchParams.maxConnections = null;
    } else {
      this.searchParams.maxConnections = direct ? 0 : null;
    }
  }

  setUpMultiCitySearch() {
    this.searchService.isFavoritesSelected = false;
    this.searchParams.chosenSearchType = FlightSearchType.multiCity;
    const cloneMultiDestination: IMultiDestination[] = _.cloneDeep(
      this.searchParams.multiDestination
    );

    this.multiCitySearch = cloneMultiDestination.map((item) => {
      item.date = moment(item.date);
      return item;
    });
    this.setMultiCityMinDate(this.multiCitySearch[0].date);
  }

  updateMultiCityDateTime(index = 0) {
    if (index === 0) {
      this.setMultiCityMinDate(this.multiCitySearch[0].date);
      const nextDay = moment(this.multiCitySearch[0].date).add(1, 'day');
      this.multiCitySearch.map((item, i) => {
        if (i !== 0 && moment(item.date).isBefore(this.multiCitySearch[0].date)) {
          item.date = nextDay;
        }
        return item;
      });
    }

    this.searchParams.multiDestination = this.multiCitySearch;
    this.searchParams.departLocation = this.multiCitySearch[0].orig;
    this.searchParams.arriveLocation = this.multiCitySearch[0].dest;
    this.searchParams.outBoundDate = this.multiCitySearch[0].date;
  }

  setMultiCityMinDate(value: moment.Moment) {
    this.minimumMultiCityDate = {
      year: value.year(),
      month: value.month() + 1,
      day: value.date()
    };
  }

  viaChanged() {
    if (!this.searchParams.showVia) {
      this.searchParams.via = '';
      if (!this.userSetDirectValue && this.searchParams.directDefaultValue) {
        this.searchParams.maxConnections = 0;
      } else if (!this.userSetDirectValue) {
        this.searchParams.maxConnections = null;
      }
    } else {
      this.searchParams.maxConnections = null;
    }
  }

  updateLocation(location, isInbound = false) {
    if (isInbound) {
      this.searchParams.arriveLocation = location;
    } else {
      this.searchParams.departLocation = location;
    }
    this.checkHotelCheckbox();
  }

  onLocationInput(value: string, isInbound = false): void {
    if (!value || value.trim() === '') {
      if (isInbound) {
        this.searchParams.arriveLocation = '';
      } else {
        this.searchParams.departLocation = '';
      }
      this.checkHotelCheckbox();
    }
  }

  get hasDepartError(): boolean {
    if (!this.searchService.show_errors) return false;
    return !!this.searchParams?.validationMessages?.some(
      (m) =>
        m.includes('departure location') ||
        m.includes('Origin and destination airport cannot be the same')
    );
  }

  get hasArriveError(): boolean {
    if (!this.searchService.show_errors) return false;
    return !!this.searchParams?.validationMessages?.some(
      (m) =>
        m.includes('arrival destination') ||
        m.includes('Origin and destination airport cannot be the same')
    );
  }

  checkHotelCheckbox() {
    this.displayHotelCheck =
      this.checkDiffBetweenDates(this.searchParams.outBoundDate, this.searchParams.inboundDate) &&
      this.searchParams.chosenSearchType === this.FlightSearchType.return &&
      !!this.searchParams.departLocation &&
      !!this.searchParams.arriveLocation;
    if (
      !this.displayHotelCheck &&
      this.toggleHotelSearchRequested.observed &&
      !this.searchParams.isFlightHotelComboSearch
    ) {
      this.hotelsearchRequested = false;
      this.toggleHotelSearchRequested.emit(this.hotelsearchRequested);
    } else if (this.displayHotelCheck || this.searchParams.isFlightHotelComboSearch) {
      this.searchService.searches[ServiceType.Flight].setHotelParams();
    }
  }

  checkDiffBetweenDates(outBoundDate: moment.Moment, inboundDate: moment.Moment): boolean {
    return !outBoundDate.isSame(inboundDate, 'date') && inboundDate > outBoundDate; // If they're not on the same date, and inbound is later than outbound, then the stay must be atleast over 1 night
  }

  toggleHotelCheckbox() {
    try {
      const town = (this.searchParams.arriveLocation as any).destination.split(',')[0];
      this.hotelTown = town.replace('City', '').trim();
    } catch (error) {
      console.error(`+++ Error finding town in airport namne: ${error} +++`);
    }

    this.hotelsearchRequested = !this.hotelsearchRequested;

    this.requireHotelCheckbox(this.hotelsearchRequested);
    this.toggleHotelSearchRequested.emit(this.hotelsearchRequested);
  }

  requireHotelCheckbox(isHotelRequired = false): void {
    if (!isHotelRequired) {
      return;
    }

    this.searchService.searches[ServiceType.Flight].setHotelParams();
  }

  handleLocationsSwapped(event: {
    depart: string | OBTAirportDetails;
    arrive: string | OBTAirportDetails;
  }) {
    this.searchParams.departLocation = event.depart;
    this.searchParams.arriveLocation = event.arrive;
  }
}
